import { useEffect, useState } from 'react';
import {useLazyGetSivChecklistQuery } from 'src/services/SivChecklist';
import {
  useGetSivReportQuery,
  usePublishSIVReportToEtmfMutation,
  useSendForReviewMutation
} from 'src/services/SivReport';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { hideLoader, showLoader } from 'src/store/Loader';
import {
  ISIVReport,
  ISIVReportAdverseEventReporting,
  ISIVReportClinicalTrialData,
  ISIVReportConfirmPage,
  ISIVReportInformedConsentVerification,
  ISIVReportMonitoring,
  ISIVReportRequirementsforEcrfcompletion,
  ISIVReportSiteAdequacy,
  ISIVReportSourceDocumentation
} from 'src/types/SIVReport';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { checkGetProperty } from 'src/util/STR';
import { useCalendarEventByVisitType } from 'src/util/useCalendarEventByVisitType';
import { updateUserAccess } from 'src/util/util';
import moment from 'moment';
import { setIsSivReportCopyOverrideData } from 'src/store/sivCheckList';

export interface ISIVReportStep1Accordions {
  clinicalTrialDetails: boolean;
  siteAdequacy: boolean;
  informedConsentVerification: boolean;
}

export interface ISIVReportStep2Accordions {
  adverseEventReporting: boolean;
  requirementsforEcrfcompletion: boolean;
  sourceDocumentation: boolean;
  monitoring: boolean;
}

export interface ISIVReportStep3Accordions {
  confirmPage: boolean;
}

export const defaultCollapsed: ISIVReportStep1Accordions = {
  clinicalTrialDetails: false,
  informedConsentVerification: false,
  siteAdequacy: false
};

export const defaultStep2Collapsed: ISIVReportStep2Accordions = {
  adverseEventReporting: false,
  requirementsforEcrfcompletion: false,
  sourceDocumentation: false,
  monitoring: false
};

export interface IStep1SivReport {
  sivReportData?: ISIVReport;
}

export type ClinicalDataKeys = keyof ISIVReportClinicalTrialData;

export type SiteAdequacyKeys = keyof ISIVReportSiteAdequacy;

export type InformedConsentVerificationKeys = keyof ISIVReportInformedConsentVerification;

export type AdverseEventReportingKeys = keyof ISIVReportAdverseEventReporting;

export type RequirementsforEcrfcompletionKeys = keyof ISIVReportRequirementsforEcrfcompletion;

export type SourceDocumentationKeys = keyof ISIVReportSourceDocumentation;

export type MonitoringKeys = keyof ISIVReportMonitoring;

export type ConfirmPageKeys = keyof ISIVReportConfirmPage;

export const clinicalDataKeys: ClinicalDataKeys[] = [
  'siv_visit_date',
  'siv_report_protocol_version',
  'siv_report_protocol_review',
  'siv_checklist_artifact_num',
  'siv_checklist_protocol_version_num',
  'siv_report_protocol_review_comm',
  'siv_report_study_objectives',
  'siv_report_study_objectives_comm',
  'siv_report_inclusion_exclusion',
  'siv_report_inclusion_exclusion_comm',
  'siv_report_study_design_timelines',
  'siv_report_study_design_timelines_comm',
  'siv_report_soa',
  'siv_report_soa_comm',
  'siv_report_enrollment_procedure',
  'siv_report_enrollment_procedure_comm',
  'siv_report_lab_procedure',
  'siv_report_lab_procedure_comm',
  'siv_report_ip_da',
  'siv_report_ip_da_comm',
  'siv_report_protocol_deviation',
  'siv_report_protocol_deviation_comm',
  'siv_report_other_procedures',
  'siv_report_other_procedures_comm',
  'siv_report_ip_intact',
  'siv_report_ip_secure',
  'siv_report_ip_storage_csp',
  'siv_report_ip_temp',
  'siv_report_refrigerator_callibration',
  'siv_report_refrigerator_notification',
  'siv_report_pharmacy_binder',
  'siv_report_pharmacist_training',
  'siv_report_irt_access',
  'siv_report_pharmacist_da_log',
  'siv_report_ip_handling_subject',
  'siv_report_report_ip_compliants',
  'siv_report_ip_return',
  'siv_report_ip_destruction_site',
  'siv_report_emergency_unblinding',
  'siv_report_maintain_blinding'
];
export const siteAdequacyKeys: SiteAdequacyKeys[] = [
  'siv_report_fics_list',
  'siv_report_fics_list_comm',
  'siv_report_contact_list',
  'siv_report_contact_list_comm',
  'siv_report_doa',
  'siv_report_doa_comm',
  'siv_report_siv_training',
  'siv_report_siv_training_comm',
  'siv_report_study_supplies',
  'siv_report_study_supplies_comm',
  'equipments'
];
export const informedConsentVerificationKeys: InformedConsentVerificationKeys[] = [
  'siv_report_protocol_version',
  'siv_checklist_protocol_version_num',
  'siv_report_ic_process',
  'siv_report_ic_process_comm',
  'siv_report_ic_template_translation',
  'siv_report_ic_template_translation_comm',
  'siv_report_ic_sdv_process',
  'siv_report_ic_sdv_process_comm'
];

export const adverseEventReportingKeys: AdverseEventReportingKeys[] = [
  'siv_report_ae_reporting',
  'siv_report_ae_reporting_comm'
];

export const requirementsforEcrfcompletionKeys: RequirementsforEcrfcompletionKeys[] = [
  'siv_report_ecrf_guidelines',
  'siv_report_ecrf_guidelines_comm',
  'siv_report_data_changes',
  'siv_report_data_changes_comm'
];

export const sourceDocumentationKeys: SourceDocumentationKeys[] = [
  'siv_report_source_doc_review',
  'siv_report_source_doc_review_comm'
];

export const monitoringKeys: MonitoringKeys[] = ['siv_report_monitoring_plan', 'siv_report_monitoring_plan_comm'];

export const confirmPageKeys: ConfirmPageKeys[] = [
  'id',
  'siv_report_prepared_print_name',
  'siv_report_prepared_signature',
  'siv_report_prepared_date',
  'siv_report_prepared_reason',
  'siv_report_reviewed_print_name',
  'siv_report_reviewed_signature',
  'siv_report_reviewed_date',
  'siv_report_reviewed_reason',
  'siv_report_approved_print_name',
  'siv_report_approved_signature',
  'siv_report_approved_date',
  'siv_report_approved_reason',
  'siv_report_additional_comments'
];

type TypeStep1SIVReportData =
  | ISIVReportClinicalTrialData
  | ISIVReportSiteAdequacy
  | ISIVReportInformedConsentVerification
  | ISIVReportAdverseEventReporting
  | ISIVReportRequirementsforEcrfcompletion
  | ISIVReportMonitoring
  | ISIVReportSourceDocumentation
  | ISIVReportConfirmPage;

export interface IStep1SIVReportData {
  clinicalTrialDetails: Partial<ISIVReportClinicalTrialData>;
  siteAdequacy: Partial<ISIVReportSiteAdequacy>;
  informedConsentVerification: Partial<ISIVReportInformedConsentVerification>;
  adverseEventReporting: Partial<ISIVReportAdverseEventReporting>;
  requirementsforEcrfcompletion: Partial<ISIVReportRequirementsforEcrfcompletion>;
  sourceDocumentation: Partial<ISIVReportSourceDocumentation>;
  monitoring: Partial<ISIVReportMonitoring>;
  confirmPage: Partial<ISIVReportConfirmPage>;
}

type DataKeys = keyof IStep1SIVReportData;

export interface Step4 {
  isSubmit: boolean;
}

export interface ISIVReportModified {
  Step1: ISIVReportStep1Accordions;
  Step2: ISIVReportStep2Accordions;
  Step3: ISIVReportStep3Accordions;
}

type CheckListModified = ISIVReportStep1Accordions | ISIVReportStep2Accordions | ISIVReportStep3Accordions;

export type ISIVReportModifiedKeys = keyof ISIVReportModified;

export type ISIVReportModifiedKeysOfKeys =
  | keyof ISIVReportStep1Accordions
  | keyof ISIVReportStep2Accordions
  | keyof ISIVReportStep3Accordions
  | keyof Step4;

const defaultChecklistModified: ISIVReportModified = {
  Step1: { clinicalTrialDetails: false, informedConsentVerification: false, siteAdequacy: false },
  Step2: {
    adverseEventReporting: false,
    monitoring: false,
    requirementsforEcrfcompletion: false,
    sourceDocumentation: false
  },
  Step3: { confirmPage: false }
};

export interface IClinicalTrialMandatory {
  siv_report_protocol_version: boolean;
  siv_report_protocol_review: boolean;
  siv_report_study_objectives: boolean;
  siv_report_inclusion_exclusion: boolean;
  siv_report_study_design_timelines: boolean;
  siv_report_soa: boolean;
  siv_report_enrollment_procedure: boolean;
  siv_report_lab_procedure: boolean;
  siv_report_ip_da: boolean;
  siv_report_protocol_deviation: boolean;
  siv_report_other_procedures: boolean;
}
export interface ISiteAdequacyMandatory {
  siv_report_fics_list: boolean;
  siv_report_contact_list: boolean;
  siv_report_doa: boolean;
  siv_report_siv_training: boolean;
  siv_report_study_supplies: boolean;
}

export interface InformedConsentVerificationMandatory {
  siv_report_protocol_version: boolean;
  siv_report_ic_process: boolean;
  siv_report_ic_template_translation: boolean;
  siv_report_ic_sdv_process: boolean;
}
export interface IAdverseEventReportingMandatory {
  siv_report_ae_reporting: boolean;
}
export interface IRequirementsforEcrfcompletionMandatory {
  siv_report_ecrf_guidelines: boolean;
  siv_report_data_changes: boolean;
}
export interface ISourceDocumentationMandatory {
  siv_report_source_doc_review: boolean;
}

export interface IMonitoringMandatory {
  siv_report_monitoring_plan: boolean;
}
export interface ISignaturePageMandatory {
  // siv_report_prepared_print_name: boolean;
  siv_report_prepared_signature: boolean;
  siv_report_prepared_date: boolean;
  siv_report_prepared_reason: boolean;
  // siv_report_reviewed_print_name: boolean;
  siv_report_reviewed_signature: boolean;
  siv_report_reviewed_date: boolean;
  siv_report_reviewed_reason: boolean;
  // siv_report_approved_print_name: boolean;
  siv_report_approved_signature: boolean;
  siv_report_approved_date: boolean;
  siv_report_approved_reason: boolean;
  siv_report_additional_comments: boolean;
}

export interface ISIVReportMandatory {
  clinicalTrialDetails: IClinicalTrialMandatory;
  siteAdequacy: ISiteAdequacyMandatory;
  informedConsentVerification: InformedConsentVerificationMandatory;
  adverseEventReporting: IAdverseEventReportingMandatory;
  requirementsforEcrfcompletion: IRequirementsforEcrfcompletionMandatory;
  sourceDocumentation: ISourceDocumentationMandatory;
  monitoring: IMonitoringMandatory;
  confirmPage: ISignaturePageMandatory;
}

export type MandatoryData =
  | IClinicalTrialMandatory
  | ISiteAdequacyMandatory
  | InformedConsentVerificationMandatory
  | IAdverseEventReportingMandatory
  | IRequirementsforEcrfcompletionMandatory
  | ISourceDocumentationMandatory
  | IMonitoringMandatory
  | ISignaturePageMandatory;

export type SIVReportMandatoryKeys = keyof ISIVReportMandatory;

export const defaultMandatory: ISIVReportMandatory = {
  clinicalTrialDetails: {
    siv_report_protocol_version: false,
    siv_report_protocol_review: false,
    siv_report_study_objectives: false,
    siv_report_inclusion_exclusion: false,
    siv_report_study_design_timelines: false,
    siv_report_soa: false,
    siv_report_lab_procedure: false,
    siv_report_enrollment_procedure: false,
    siv_report_ip_da: false,
    siv_report_protocol_deviation: false,
    siv_report_other_procedures: false
  },
  siteAdequacy: {
    siv_report_fics_list: false,
    siv_report_contact_list: false,
    siv_report_doa: false,
    siv_report_siv_training: false,
    siv_report_study_supplies: false
  },
  informedConsentVerification: {
    siv_report_protocol_version: false,
    siv_report_ic_process: false,
    siv_report_ic_template_translation: false,
    siv_report_ic_sdv_process: false
  },
  adverseEventReporting: {
    siv_report_ae_reporting: false
  },
  requirementsforEcrfcompletion: {
    siv_report_ecrf_guidelines: false,
    siv_report_data_changes: false
  },
  sourceDocumentation: {
    siv_report_source_doc_review: false
  },
  monitoring: {
    siv_report_monitoring_plan: false
  },
  confirmPage: {    
    siv_report_prepared_signature: false,
    siv_report_prepared_date: false,
    siv_report_prepared_reason: false,    
    siv_report_reviewed_signature: false,
    siv_report_reviewed_date: false,
    siv_report_reviewed_reason: false,    
    siv_report_approved_signature: false,
    siv_report_approved_date: false,
    siv_report_approved_reason: false,
    siv_report_additional_comments: false
  }
};

export const useSivReport = () => {
  const dispatch = useAppDispatch();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [sivReportSavedId, setSivReportSavedId] = useState<string>(''); /** To handle query menus */
  const selectedRowData: any = useAppSelector(state => state.sivReport.selectedRowData);
  const isSivReportCopyOverrideData: any = useAppSelector(state => state.sivCheckList.isSivReportCopyOverrideData);

  const { data, error, isError, isLoading, refetch } = useGetSivReportQuery({
    monitoring_visit_id: selectedRowData.id,
    site_id: selectedSite,
    study_id: studyId,
    country: selectedCountry,
    application: process.env.REACT_APP_NAME
  });

  const [mandatory, setMandatory] = useState<ISIVReportMandatory>(defaultMandatory);

  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set<number>());
  const [step1SivReport, setStep1SivReport] = useState<ISIVReportStep1Accordions>({
    ...defaultCollapsed,
    clinicalTrialDetails: true
  });

  const [step2SivReport, setStep2SivReport] = useState<ISIVReportStep2Accordions>({
    ...defaultStep2Collapsed,
    adverseEventReporting: true
  });

  const [step3SivReport, setStep3SivReport] = useState<ISIVReportStep3Accordions>({
    ...defaultStep2Collapsed,
    confirmPage: true
  });

  const [sivReportModified, setSIVReportModified] = useState<ISIVReportModified>(defaultChecklistModified);

  const [saveDisable, setSaveDisable] = useState(
    !data?.is_published && data?.siv_report_approved_signature && data?.siv_report_approved_signature != ''
      ? true
      : false
  );

  const [isUpdate, setIsUpdate] = useState(data?.study_id ? true : false);
  const isPublished = data?.is_published ? true : false;
  const getValues = <T, K extends keyof T>(dta: ISIVReport, keys: K[]): T => {
    const newData: Partial<T> = {};
    keys.forEach(key => {
      const val: any = dta[key as keyof ISIVReport] || '';
      newData[key] = val;
    });
    return newData as T;
  };
  const [sivReportData, setSIVReportData] = useState<Partial<IStep1SIVReportData>>({});

  const [sivReportModifiedData, setSIVReportModifiedData] = useState<Partial<IStep1SIVReportData>>({});

  const [getSivCheckList, { data: sivCheckListData, error: sivCheckListError, isLoading: sivCheckListIsLoading }] =
    useLazyGetSivChecklistQuery();
  
  useEffect(() => {
    updateUserAccess(sivCheckListError, dispatch);
  }, [sivCheckListError]);

  useEffect(() => {
    if (sivCheckListIsLoading || isLoading ) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
    return () => {
      dispatch(hideLoader());
    };
  }, [sivCheckListIsLoading,isLoading]);

  useEffect(() => {
    getSivCheckList({
      monitoring_visit_id: selectedRowData.id,
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME
    });
  }, [error]);

  useEffect(() => {
    if(isSivReportCopyOverrideData){
    getSivCheckList({
      monitoring_visit_id: selectedRowData.id,
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME
    });
  }
  }, [isSivReportCopyOverrideData]);
 
  const getDataFromSivChecklistData = (sivCheckListData: any) => {
    if (sivCheckListData && sivCheckListData.is_published) {
      return {
        siv_visit_date: sivCheckListData['siv_visit_date'] || '',
        siv_checklist_protocol_version_num: sivCheckListData['siv_checklist_protocol_version_num'] || '',
        siv_report_protocol_version: sivCheckListData['siv_checklist_protocol_version'] || '',
        siv_report_protocol_review: sivCheckListData['siv_checklist_source_doc_review'] || '',
        siv_report_study_objectives: sivCheckListData['siv_checklist_study_objectives'] || '',
        siv_report_inclusion_exclusion: sivCheckListData['siv_checklist_inclusion_exclusion'] || '',
        siv_report_study_design_timelines: sivCheckListData['siv_checklist_study_design_timelines'] || '',
        siv_report_soa: sivCheckListData['siv_checklist_soa'] || '',
        siv_report_enrollment_procedure: sivCheckListData['siv_checklist_enrollment_procedure'] || '',
        siv_report_lab_procedure: sivCheckListData['siv_checklist_lab_procedure'] || '',
        siv_report_ip_da: sivCheckListData['siv_checklist_ip_da'] || '',
        siv_report_ip_secure: sivCheckListData['siv_checklist_ip_secure'] || '',
        siv_report_ip_storage_csp: sivCheckListData['siv_checklist_ip_secure'] || '',
        siv_report_refrigerator_notification: sivCheckListData['siv_checklist_soa'] || '',
        siv_report_protocol_deviation: sivCheckListData['siv_checklist_protocol_deviation'] || '',
        siv_report_other_procedures: sivCheckListData['siv_checklist_other_procedures'] || '',
        siv_report_fics_list: sivCheckListData['siv_checklist_fics_list'] || '',
        siv_report_contact_list: sivCheckListData['siv_checklist_contact_list'] || '',
        siv_report_doa: sivCheckListData['siv_checklist_doa'] || '',
        siv_report_siv_training: sivCheckListData['siv_checklist_siv_training'] || '',
        siv_report_study_supplies: sivCheckListData['siv_checklist_study_supplies'] || ''
      };
    } else {
      dispatch(setIsSivReportCopyOverrideData(false));
      return {} as ISIVReport;
    }
  };

  const { getMonitoringEventData } = useCalendarEventByVisitType('Site Initiation Visit');
  const { userPreferedDateFormat } = usePreferedDateFormat();
  useEffect(() => {   
    if (!isLoading) {
      (async () => {
        const sivEventStartDate = await getMonitoringEventData();

        const dataFromSIVChecklist: any = getDataFromSivChecklistData(sivCheckListData);
        const sivData: ISIVReport =
          data && !isError && !isSivReportCopyOverrideData ? data : (dataFromSIVChecklist as ISIVReport);

        let clinical = null;
        let site = null;
        if (sivData) {
          clinical = getValues<ISIVReportClinicalTrialData, ClinicalDataKeys>(sivData, clinicalDataKeys);
        }

        if (sivData?.siv_report_fics_list == null) {
          site = getValues<any, any>(dataFromSIVChecklist, siteAdequacyKeys);
        } else {
          site = getValues<ISIVReportSiteAdequacy, SiteAdequacyKeys>(sivData, siteAdequacyKeys);
        }
        let informedConsent = null;
        if (sivData?.siv_report_ic_process == null) {
          informedConsent = getValues<any, any>(dataFromSIVChecklist, informedConsentVerificationKeys);
        } else {
          informedConsent = getValues<ISIVReportInformedConsentVerification, InformedConsentVerificationKeys>(
            sivData,
            informedConsentVerificationKeys
          );
        }
        let adverseEvent = null;
        if (sivData?.siv_report_ae_reporting == null) {
          adverseEvent = getValues<any, any>(dataFromSIVChecklist, adverseEventReportingKeys);
        } else {
          adverseEvent = getValues<ISIVReportAdverseEventReporting, AdverseEventReportingKeys>(
            sivData,
            adverseEventReportingKeys
          );
        }
        let requirementsforEcr = null;
        if (sivData?.siv_report_ecrf_guidelines == null) {
          requirementsforEcr = getValues<any, any>(dataFromSIVChecklist, requirementsforEcrfcompletionKeys);
        } else {
          requirementsforEcr = getValues<ISIVReportRequirementsforEcrfcompletion, RequirementsforEcrfcompletionKeys>(
            sivData,
            requirementsforEcrfcompletionKeys
          );
        }

        let sourceDocu = null;
        if (sivData?.siv_report_source_doc_review == null) {
          sourceDocu = getValues<any, any>(dataFromSIVChecklist, sourceDocumentationKeys);
        } else {
          sourceDocu = getValues<ISIVReportSourceDocumentation, SourceDocumentationKeys>(
            sivData,
            sourceDocumentationKeys
          );
        }
        let monitor = null;
        if (sivData?.siv_report_monitoring_plan == null) {
          monitor = getValues<any, any>(dataFromSIVChecklist, monitoringKeys);
        } else {
          monitor = getValues<ISIVReportMonitoring, MonitoringKeys>(sivData, monitoringKeys);
        }
        const confirm = getValues<ISIVReportConfirmPage, ConfirmPageKeys>(sivData, confirmPageKeys);

        const state: Partial<IStep1SIVReportData> = {
          clinicalTrialDetails: {
            ...clinical,
            siv_visit_date: sivEventStartDate
              ? moment(new Date(`${sivEventStartDate}Z`)).format(userPreferedDateFormat.toUpperCase())
              : ''
          },
          siteAdequacy: site,
          informedConsentVerification: informedConsent,
          adverseEventReporting: adverseEvent,
          requirementsforEcrfcompletion: requirementsforEcr,
          sourceDocumentation: sourceDocu,
          monitoring: monitor,
          confirmPage: confirm
        };
        Object.entries(sivReportModifiedData).forEach(item => {
          state[item[0] as DataKeys] = item[1];
        });
        setSIVReportData(state);
        setSIVReportModifiedData({});
        dispatch(setIsSivReportCopyOverrideData(false));
        if (data) {
          setIsUpdate(data?.study_id ? true : false);
          setSaveDisable(
            data?.siv_report_approved_signature && data?.siv_report_approved_signature !== '' && !data?.is_published
              ? true
              : false
          );
          setSivReportSavedId(data.id);
        }
      })();
    }
  }, [data, sivCheckListData, isLoading, isError, isSivReportCopyOverrideData]);

  const toggle = (key: keyof ISIVReportStep1Accordions, expanded: boolean) => {
    if (!expanded) {
      setStep1SivReport(prev => ({ ...prev, [key]: expanded }));
    } else {
      setStep1SivReport({ ...defaultCollapsed, [key]: expanded });
    }
  };

  const toggleStep2 = (key: keyof ISIVReportStep2Accordions, expanded: boolean) => {
    if (!expanded) {
      setStep2SivReport(prev => ({ ...prev, [key]: expanded }));
    } else {
      setStep2SivReport({ ...defaultStep2Collapsed, [key]: expanded });
    }
  };

  const toggleStep3 = (key: keyof ISIVReportStep3Accordions, expanded: boolean) => {
    setStep3SivReport(prev => ({ ...prev, [key]: expanded }));
  };

  const updateSIVReportMandatoryData = (key: keyof ISIVReportMandatory, value: MandatoryData) => {
    setMandatory(prev => ({ ...prev, [key]: value }));
  };

  const updateStep1SIVReportData = (key: keyof IStep1SIVReportData, value: TypeStep1SIVReportData) => {
    setSIVReportData(prev => ({ ...prev, [key]: value }));
  };

  const updateClinicalData = (dta: ISIVReportClinicalTrialData) => {
    setSIVReportModified(prev => ({ ...prev, Step1: { ...prev.Step1, clinicalTrialDetails: true } }));
    updateStep1SIVReportData('clinicalTrialDetails', dta);
  };

  const updateSiteAdequacyData = (dta: ISIVReportSiteAdequacy) => {
    setSIVReportModified(prev => ({ ...prev, Step1: { ...prev.Step1, siteAdequacy: true } }));
    updateStep1SIVReportData('siteAdequacy', dta);
  };

  const updateIinformedConsentVerification = (dta: ISIVReportInformedConsentVerification) => {
    setSIVReportModified(prev => ({ ...prev, Step1: { ...prev.Step1, informedConsentVerification: true } }));
    updateStep1SIVReportData('informedConsentVerification', dta);
  };

  const updateAdverseEventReporting = (dta: ISIVReportAdverseEventReporting) => {
    setSIVReportModified(prev => ({ ...prev, Step2: { ...prev.Step2, adverseEventReporting: true } }));
    updateStep1SIVReportData('adverseEventReporting', dta);
  };

  const updateRequirementsforEcrfCompletion = (dta: ISIVReportRequirementsforEcrfcompletion) => {
    setSIVReportModified(prev => ({ ...prev, Step2: { ...prev.Step2, requirementsforEcrfcompletion: true } }));
    updateStep1SIVReportData('requirementsforEcrfcompletion', dta);
  };

  const updateSourceDocumentation = (dta: ISIVReportSourceDocumentation) => {
    setSIVReportModified(prev => ({ ...prev, Step2: { ...prev.Step2, sourceDocumentation: true } }));
    updateStep1SIVReportData('sourceDocumentation', dta);
  };
  const updateMonitoring = (dta: ISIVReportMonitoring) => {
    setSIVReportModified(prev => ({ ...prev, Step2: { ...prev.Step2, monitoring: true } }));
    updateStep1SIVReportData('monitoring', dta);
  };
  const updateConfirmPage = (dta: ISIVReportConfirmPage) => {
    setSIVReportModified(prev => ({ ...prev, Step3: { ...prev.Step3, confirmPage: true } }));
    updateStep1SIVReportData('confirmPage', dta);
  };

  const getData = <T, K extends keyof T>(dta: T, key: K): T[K] => {
    return (dta && dta[key] ? dta[key] : {}) as T[K];
  };

  const setIsChange = (step: ISIVReportModifiedKeys, key: ISIVReportModifiedKeysOfKeys, val: boolean): void => {
    setSIVReportModified(prev => {
      return { ...prev, [step]: { ...prev[step], [key]: val } };
    });
  };

  const notifyUser = data?.notify_user;

  const keys = Object.keys(sivReportData).length;

  const isModified = (obj: CheckListModified): boolean => Object.entries(obj).some(item => item[1] === true);

  const getModifiedKey = (obj: any, val: any) => Object.keys(obj).find(key => obj[key as keyof any] === val);

  const getModifiedKeys = (obj: any, val: any) => Object.keys(obj).filter(key => obj[key as keyof any] === val);

  const getSivReportData = (key: ISIVReportModifiedKeysOfKeys, _: boolean) => {
    const step1Keys = getModifiedKeys(sivReportModified.Step1, true);
    const step2Keys = getModifiedKeys(sivReportModified.Step2, true);
    const step3Keys = getModifiedKeys(sivReportModified.Step3, true);
    const allModifiedKeys = [...step1Keys, ...step2Keys, ...step3Keys].filter(item => item !== key);
    const modifiedData: Partial<IStep1SIVReportData> = {};
    allModifiedKeys.forEach(modifiedKey => {
      modifiedData[modifiedKey as DataKeys] = sivReportData[modifiedKey as DataKeys];
    });
    setSIVReportModifiedData(modifiedData);
    refetch();
  };

  const checkChanged = (move: boolean): boolean => {
    const step1 = isModified(sivReportModified.Step1);
    const step2 = isModified(sivReportModified.Step2);
    const step3 = isModified(sivReportModified.Step3);
    if (move && (step1 || step2 || step3)) {
      const step1Key = getModifiedKey(sivReportModified.Step1, true) as keyof ISIVReportStep1Accordions;
      const step2Key = getModifiedKey(sivReportModified.Step2, true) as keyof ISIVReportStep2Accordions;
      const step3Key = getModifiedKey(sivReportModified.Step3, true) as keyof ISIVReportStep3Accordions;
      const page = step1 ? 0 : step2 ? 1 : step3 ? 2 : activeStep;
      if (step1Key) {
        toggle(step1Key, true);
      } else if (step2Key) {
        toggleStep2(step2Key, true);
      } else if (step3Key) {
        toggleStep3(step3Key, true);
      }
      setActiveStep(page);
    }
    return step1 || step2 || step3;
  };

  const checkMandatoryFields = (): boolean => {
    // Step 1 clinicalTrialDetails siteAdequacy informedConsentVerification
    const clinicalTrialDetailsKeys = getModifiedKeys(mandatory.clinicalTrialDetails, true);
    const siteAdequacyKeys = getModifiedKeys(mandatory.siteAdequacy, true);
    const informedConsentVerificationKeys = getModifiedKeys(mandatory.informedConsentVerification, true);
    // Step 2 adverseEventReporting requirementsforEcrfcompletion sourceDocumentation monitoring
    const adverseEventReportingKeys = getModifiedKeys(mandatory.adverseEventReporting, true);
    const requirementsforEcrfcompletionKeys = getModifiedKeys(mandatory.requirementsforEcrfcompletion, true);
    const sourceDocumentationKeys = getModifiedKeys(mandatory.sourceDocumentation, true);
    const monitoringKeys = getModifiedKeys(mandatory.monitoring, true);
    //Step 3   confirmPage;
    const confirmPageKeys = getModifiedKeys(mandatory.confirmPage, true);

    return [
      ...clinicalTrialDetailsKeys,
      ...siteAdequacyKeys,
      ...informedConsentVerificationKeys,
      ...adverseEventReportingKeys,
      ...requirementsforEcrfcompletionKeys,
      ...sourceDocumentationKeys,
      ...monitoringKeys,
      ...confirmPageKeys
    ].length === 0
      ? false
      : true;
  };

  const validateEmpty = (obj: any, keys: string[]): boolean => {
    return keys.every(key => {
      try {
        const val: string | any = checkGetProperty(obj, key);
        if (val && val !== '') {
          return true;
        }
      } catch (e) {
        console.error(e);
      }
      return false;
    });
  };

  const validate = (obj: any, keys: string[]): boolean => {
    return keys.every(key => {
      try {
        if (!key.includes('_comm')) {
          const val: string | any = checkGetProperty(obj, key);
          if (val && val !== '' && (val === 'Y' || val === 'N' || val === 'NA')) {
            return true;
          }
        } else {
          return true;
        }
      } catch (e) {
        console.error(e);
      }
      return false;
    });
  };

  const validateMandatoryFields = (move: boolean) => {
    const clinicalTrialDetails = validate(
      sivReportData.clinicalTrialDetails,
      Object.keys(mandatory.clinicalTrialDetails)
    );
    const siteAdequacy = validate(sivReportData.siteAdequacy, Object.keys(mandatory.siteAdequacy));
    const informedConsentVerification = validate(
      sivReportData.informedConsentVerification,
      Object.keys(mandatory.informedConsentVerification)
    );
    const step1Key = !clinicalTrialDetails
      ? 'clinicalTrialDetails'
      : !siteAdequacy
      ? 'siteAdequacy'
      : !informedConsentVerification
      ? 'informedConsentVerification'
      : null;
    if (step1Key) {
      if (move) {
        toggle(step1Key, true);
        setActiveStep(0);
      }
      return false;
    }
    const adverseEventReporting = validate(
      sivReportData.adverseEventReporting,
      Object.keys(mandatory.adverseEventReporting)
    );
    const requirementsforEcrfcompletion = validate(
      sivReportData.requirementsforEcrfcompletion,
      Object.keys(mandatory.requirementsforEcrfcompletion)
    );
    const sourceDocumentation = validate(sivReportData.sourceDocumentation, Object.keys(mandatory.sourceDocumentation));
    const monitoring = validate(sivReportData.monitoring, Object.keys(mandatory.monitoring));
    const step2Key = !adverseEventReporting
      ? 'adverseEventReporting'
      : !requirementsforEcrfcompletion
      ? 'requirementsforEcrfcompletion'
      : !sourceDocumentation
      ? 'sourceDocumentation'
      : !monitoring
      ? 'monitoring'
      : null;
    if (step2Key) {
      if (move) {
        toggleStep2(step2Key, true);
        setActiveStep(1);
      }
      return false;
    }
    return true;
  };

  const validateMandatoryFieldsForSign = (move: boolean) => {
    const clinicalTrialDetails = validate(
      sivReportData.clinicalTrialDetails,
      Object.keys(mandatory.clinicalTrialDetails)
    );
    const siteAdequacy = validate(sivReportData.siteAdequacy, Object.keys(mandatory.siteAdequacy));
    const informedConsentVerification = validate(
      sivReportData.informedConsentVerification,
      Object.keys(mandatory.informedConsentVerification)
    );
    const step1Key = !clinicalTrialDetails
      ? 'clinicalTrialDetails'
      : !siteAdequacy
      ? 'siteAdequacy'
      : !informedConsentVerification
      ? 'informedConsentVerification'
      : null;
    if (step1Key) {
      if (move) {
        toggle(step1Key, true);
        setActiveStep(0);
      }
      return false;
    }
    const adverseEventReporting = validate(
      sivReportData.adverseEventReporting,
      Object.keys(mandatory.adverseEventReporting)
    );
    const requirementsforEcrfcompletion = validate(
      sivReportData.requirementsforEcrfcompletion,
      Object.keys(mandatory.requirementsforEcrfcompletion)
    );
    const sourceDocumentation = validate(sivReportData.sourceDocumentation, Object.keys(mandatory.sourceDocumentation));
    const monitoring = validate(sivReportData.monitoring, Object.keys(mandatory.monitoring));
    const step2Key = !adverseEventReporting
      ? 'adverseEventReporting'
      : !requirementsforEcrfcompletion
      ? 'requirementsforEcrfcompletion'
      : !sourceDocumentation
      ? 'sourceDocumentation'
      : !monitoring
      ? 'monitoring'
      : null;
    if (step2Key) {
      if (move) {
        toggleStep2(step2Key, true);
        setActiveStep(1);
      }
      return false;
    }
    if (!validateEmpty(sivReportData.confirmPage, ['siv_report_additional_comments'])) {
      if (move) {
        toggleStep3('confirmPage', true);
        setActiveStep(2);
      }
      return false;
    }
    return true;
  };

  const isMandatoryFields = checkMandatoryFields();

  const validateMandatory = validateMandatoryFields(false);

  const isChanged = checkChanged(false);
  const [
    publishSivReportToEtmf,
    {
      isLoading: publishSivReportLoading,
      isSuccess: publishSivReportSuccess,
      data: publishSivReportData,
      isError: publishSivReportIsError,
      error: publishSivReportError
    }
  ] = usePublishSIVReportToEtmfMutation();

  const [
    sendForReviewFunc,
    {
      isLoading: sendForReviewIsLoading,
      isSuccess: sendForReviewIsSuccess,
      data: sendForReviewData,
      isError: sendForReviewIsError,
      error: sendForReviewError
    }
  ] = useSendForReviewMutation();

  return {
    keys,
    step1SivReport,
    toggle,
    step2SivReport,
    toggleStep2,
    step3SivReport,
    toggleStep3,
    isUpdate,
    setIsUpdate,
    clinicalTrialData: getData(sivReportData, 'clinicalTrialDetails') as ISIVReportClinicalTrialData,
    siteAdequacyData: getData(sivReportData, 'siteAdequacy') as ISIVReportSiteAdequacy,
    informedConsentVerificationData: getData(
      sivReportData,
      'informedConsentVerification'
    ) as ISIVReportInformedConsentVerification,
    adverseEventReportingData: getData(sivReportData, 'adverseEventReporting') as ISIVReportAdverseEventReporting,
    requirementsforEcrfCompletionData: getData(
      sivReportData,
      'requirementsforEcrfcompletion'
    ) as ISIVReportRequirementsforEcrfcompletion,
    sourceDocumentationData: getData(sivReportData, 'sourceDocumentation') as ISIVReportSourceDocumentation,
    monitoringData: getData(sivReportData, 'monitoring') as ISIVReportMonitoring,
    confirmPageData: getData(sivReportData, 'confirmPage') as ISIVReportConfirmPage,
    updateSiteAdequacyData,
    updateIinformedConsentVerification,
    updateClinicalData,
    updateAdverseEventReporting,
    updateRequirementsforEcrfCompletion,
    updateSourceDocumentation,
    updateMonitoring,
    updateConfirmPage,
    setSaveDisable,
    saveDisable,
    setIsChange,
    sivReportModified,
    activeStep,
    setActiveStep,
    skipped,
    setSkipped,
    selectedSite,
    studyId,
    selectedCountry,
    getSivReportData,
    checkChanged,
    isChanged,
    updateSIVReportMandatoryData,
    mandatory,
    isMandatoryFields,
    validateMandatory,
    validateMandatoryFields,
    validateMandatoryFieldsForSign,
    sivReportSavedId,
    data,
    isError,
    error,
    isLoading,
    publishSivReportToEtmf,
    publishSivReportLoading,
    publishSivReportSuccess,
    publishSivReportData,
    publishSivReportIsError,
    publishSivReportError,
    isPublished,
    sendForReviewFunc,
    sendForReviewIsLoading,
    sendForReviewIsSuccess,
    sendForReviewData,
    sendForReviewIsError,
    sendForReviewError,
    notifyUser
  };
};
